import { useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, LinearProgress, Paper, Snackbar, SnackbarCloseReason, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { VTextField, VForm, useVForm, IVFormErrors, AutoCompleteColhedor, VFieldMoney, VTextFieldData } from '../../../shared/forms';
import { FerramentasDeDetalhe } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { AdiantamentoService, IDetalheAdiantamentos } from '../../../shared/services/api/AdiantamentoService';
import dayjs from 'dayjs';

const formValidationSchema: yup.ObjectSchema<Omit<IDetalheAdiantamentos, 'nome' | 'status'>> = yup.object().shape({
  guuidcolhedor: yup.string().required(),
  datadesconto: yup.date().required(),
  historico: yup.string().optional().default(''),
  valor: yup.number().required().moreThan(0),
});

export const DetalheDeAdiantamento: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const { formRef, save, saveAndClose } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [nome, setNome] = useState('');
  const [fstatus, setFstatus] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error'  | 'info'  | 'success'>('warning');  
  const [openConfirm, setOpenConfirm] = useState(false);
 
  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event, 
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if(tms === 'success'){ 
      navigate('/adiantamentos');
    }
  };

  const handleDelete = (id: string) => {
    setOpenConfirm(true); 
  };

  const handleConfirmDelete = () => {
    AdiantamentoService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOpenConfirm(false);
          setTms('success');
          setMsg('Registro apagado com sucesso!');
          setOpenmsg(true);
        }
      });
  };

  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };
  
  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      AdiantamentoService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setNome('Adiantamento de : '+ result.nome);
            setFstatus(result.status);
            formRef.current?.setData(result);
          }
        });
    } else {
      formRef.current?.setData({
        datadesconto: dayjs(),
        historico: '',
        valor: 0,
      });
    }
  }, [id]);

  
  const handleSave = (dados: IDetalheAdiantamentos) => {
    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'novo') {
          AdiantamentoService
            .create(dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                setTms('error'); 
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
              }
            });
        } else {
          AdiantamentoService
            .updateById(id, dadosValidados )
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Alteração concluída com sucesso!');
                setOpenmsg(true);
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleNomeColhedor = (nome: string | undefined) => {
    if (nome != null) {
      setNome('Admiantamento: ' + nome);
    }
  }; 

  return (
    <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Admiantamento' : nome}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoSalvarCarregando = {isLoading}
          mostrarBotaoSalvarEFecharCarregando = {isLoading}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo' ? (fstatus ? true : isLoading) : false}
          mostrarBotaoSalvar={id !== 'novo' ? (fstatus ? true : isLoading) : true}
          mostrarBotaoApagarCarregando = {isLoading}
          mostrarBotaoImpressao={false}
          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate('/adiantamentos')}
          mostrarBotaoVoltarCarregando = {isLoading}
          aoClicarEmApagar={() => handleDelete(id)}
          aoClicarEmNovo={() => navigate('/adiantamenots/detalhe/novo')}
          mostrarBotaoNovoCarregando = {isLoading}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave} placeholder=''onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg}  autoHideDuration={900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Snackbar open={openConfirm} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            severity="warning"  
            variant="filled"
            sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleConfirmDelete}>
              Sim
                </Button>
                <Button color="inherit" size="small" onClick={handleCancelDelete}>
              Nâo
                </Button>
              </>
            }
          >
            Tem certeza que deseja excluir este registro?
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
          <Grid container direction="column" padding={2} spacing={2}>
            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}
            <Grid item>
              <Typography variant='h6'>Geral</Typography>
            </Grid>
            <Grid container item direction="row" spacing={2}>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
            <Grid container item direction="row" spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={4} xl={12}>
                <AutoCompleteColhedor
                  isExternalLoading={id === 'novo' ? isLoading : true}
                  nome={handleNomeColhedor}
                /> 
              </Grid>
              <Grid item xs={6} sm={12} md={2.5} lg={4} xl={4}>
                    <VTextFieldData
                      fullWidth
                      name='datalancamento'
                      label='Lançamento'
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={6} sm={12} md={2.5} lg={4} xl={4}>
                    <VTextFieldData
                      fullWidth
                      name='datadesconto'
                      label='Desconto'
                      disabled={id !== 'novo' ? (fstatus ?  isLoading : true) : false}
                    />
                  </Grid>
                 <Grid item xs={12} sm={2} md={2.5} lg={4} xl={4}>
                    <VFieldMoney
                      fullWidth
                      size='small'
                      name='valor'
                      label='Valor'
                      disabled={id !== 'novo' ? (fstatus ?  isLoading : true) : false}
                    />
                  </Grid>   
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <VTextField
                  fullWidth
                  name='historico'
                  label='Historico'
                  multiline
                  rows={4}
                  disabled={id !== 'novo' ? (fstatus ?  isLoading : true) : false}
                />
              </Grid>
              </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </VForm> 

    </LayoutBaseDePagina>
  );
};